<script lang="ts" setup>
import type {VehicleIdentifierPlateSize} from '@/constants/vehicle-identifier-plate-size';
import type {PropType} from "vue";
import { useMessage, type MessageApi } from "naive-ui";
import { useI18n } from "vue-i18n";

const props = defineProps({
  chassisNumber: {
    type: String,
    required: false,
  },
  size: {
    type: String as PropType<VehicleIdentifierPlateSize>,
    default: "medium",
    required: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
    required: false,
  },
  truncate: {
    type: Boolean,
    default: true,
    required: false,
  },
});

const message: MessageApi | null = props.readOnly ? null : useMessage();
const { t } = useI18n();

const lightColorCharacters = () => (props.truncate ? '...' : '');

const darkColorCharacters = () => {
  if (!props.chassisNumber) return '';

  return props.truncate ? props.chassisNumber.slice(-6) : props.chassisNumber;
}

const copyChassisNumber = async () => {
  if (!props.chassisNumber) {
    return;
  }

  try {
    await navigator.clipboard.writeText(props.chassisNumber);
    message?.success(t('components.chassis_number.copy.success'));
  } catch {
    message?.error(t('components.chassis_number.copy.failed'));
  }
};

const handleClick = (event: MouseEvent) => {
  if (props.readOnly) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  copyChassisNumber();
};

const handleKey = (event: KeyboardEvent) => {
  if (props.readOnly) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  copyChassisNumber();
};
</script>

<template>
  <div
    v-if="props.chassisNumber"
    class="vehicle-identification-plate-wrapper"
    :class="{
      'small': props.size === 'small',
      'large': props.size === 'large',
      'huge': props.size === 'huge',
      'read-only': props.readOnly,
    }"
    :role="props.readOnly ? undefined : 'button'"
    :tabindex="props.readOnly ? undefined : 0"
    @click="handleClick"
    @keydown.enter="handleKey"
    @keydown.space="handleKey"
  >
    <div class="vehicle-identification-plate">
      <div class="vehicle-identification-plate__character light" v-for="character in lightColorCharacters().split('')">
        {{ character }}
      </div>

      <div class="vehicle-identification-plate__character" v-for="character in darkColorCharacters().split('')">
        {{ character }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.vehicle-identification-plate-wrapper {
  border: 1px solid var(--dis-chassis-bordercolor);
  background-color: var(--dis-chassis-backgroundcolor);
  box-shadow: 0 0 0 1px var(--dis-chassis-backgroundcolor);
  cursor: pointer;
  user-select: none;

  &.read-only {
    cursor: inherit;
  }

  &:focus-visible {
    outline: 2px solid var(--dis-color-focus, currentColor);
    outline-offset: 2px;
  }

  .vehicle-identification-plate {
    .vehicle-identification-plate__character {
      color: var(--dis-chassis-textcolor);

      &.light {
        color: var(--dis-chassis-textcolorlight);
      }
    }
  }
}
</style>
